import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Calendar, Download, Loader2, BarChart3, Utensils, FileText, Store,
  Gift, Banknote, CheckCircle2, Clock, PiggyBank, Repeat
} from 'lucide-react';
import Logo from '../Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'ytd', label: 'Year to Date' },
  { key: 'all_time', label: 'All Time' },
];

export default function CharityDonations({ charity, onLogout }) {
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [retailers, setRetailers] = useState({ count: 0, retailers: [] });
  const [impact, setImpact] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [period, setPeriod] = useState('all_time');
  const [chartMode, setChartMode] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('charity_session_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const loadPeriodData = useCallback(async (p) => {
    try {
      const [summaryRes, retailersRes, impactRes] = await Promise.all([
        fetch(`${API_URL}/api/charity-portal/donations/summary?period=${p}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/charity-portal/donations/retailers?period=${p}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/charity-portal/impact?period=${p}`, { headers: getAuthHeaders() }),
      ]);
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (retailersRes.ok) setRetailers(await retailersRes.json());
      if (impactRes.ok) setImpact(await impactRes.json());
    } catch (error) {
      console.error('Failed to load donation data:', error);
      toast.error('Failed to load donation data');
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [monthlyRes, docsRes, payoutsRes] = await Promise.all([
          fetch(`${API_URL}/api/charity-portal/donations/monthly?months=12`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/charity-portal/documents`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/charity-portal/payouts`, { headers: getAuthHeaders() }),
        ]);
        if (monthlyRes.ok) {
          const data = await monthlyRes.json();
          setMonthly(data.monthly_data || []);
        }
        if (docsRes.ok) {
          const data = await docsRes.json();
          setDocuments(data.documents || []);
        }
        if (payoutsRes.ok) {
          setPayouts(await payoutsRes.json());
        }
      } catch (e) {
        console.error(e);
      }
      await loadPeriodData('all_time');
      setLoading(false);
    };
    init();
  }, [getAuthHeaders, loadPeriodData]);

  const changePeriod = async (p) => {
    setPeriod(p);
    await loadPeriodData(p);
  };

  const downloadDoc = async (doc) => {
    const url = doc.type === 'annual'
      ? `${API_URL}/api/charity-portal/documents/annual?year=${doc.period}`
      : `${API_URL}/api/charity-portal/documents/receipt?period=${doc.period}`;
    const key = `${doc.type}-${doc.period}`;
    setDownloading(key);
    try {
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = doc.type === 'annual'
        ? `DealShaq_Annual_${doc.period}.pdf`
        : `DealShaq_Receipt_${doc.period}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Could not download document');
    } finally {
      setDownloading('');
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // ---- Contribution rollup (round-ups + direct gifts + grocer-partner donations) ----
  const total = summary?.total_charitable_contributions || 0;
  const pct = (v) => (total > 0 ? Math.round(((v || 0) / total) * 100) : 0);
  const sources = [
    { key: 'round_ups', label: 'Round-Ups', desc: 'shopper checkout round-ups', color: 'bg-blue-500', text: 'text-blue-600', icon: Repeat },
    { key: 'direct_gifts', label: 'Direct Gifts', desc: "DealShaq's gift on shopper orders", color: 'bg-indigo-500', text: 'text-indigo-600', icon: Gift },
    { key: 'grocer_partner', label: 'Grocer-Partner Donations', desc: 'from supporting grocers', color: 'bg-purple-500', text: 'text-purple-600', icon: Store },
  ];

  // ---- Monthly / quarterly chart data ----
  const quarterly = Object.values(monthly.reduce((acc, m) => {
    if (!m.month) return acc;
    const [y, mo] = m.month.split('-');
    const q = Math.floor((parseInt(mo, 10) - 1) / 3) + 1;
    const key = `${y}-Q${q}`;
    if (!acc[key]) acc[key] = { label: `Q${q} ${y}`, amount: 0, orders: 0 };
    acc[key].amount += m.amount;
    acc[key].orders += m.orders;
    return acc;
  }, {}));
  const chartData = chartMode === 'quarterly'
    ? quarterly
    : monthly.map((m) => ({ label: formatMonth(m.month), amount: m.amount, orders: m.orders }));
  const chartMax = Math.max(...chartData.map((d) => d.amount), 1);

  const showNewReturning = period !== 'all_time' && summary?.new_contributors != null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="charity-donations">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/charity" className="text-gray-600 hover:text-gray-900" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-semibold text-gray-900">Donations & Reports</h1>
              <p className="text-xs text-gray-500">DealShaq Charity Impact Dashboard</p>
            </div>
          </div>
          <Logo size="small" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <>
            {/* Period selector */}
            <div className="flex flex-wrap gap-2" data-testid="period-selector">
              {PERIODS.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={period === p.key ? 'default' : 'outline'}
                  className={period === p.key ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={() => changePeriod(p.key)}
                  data-testid={`period-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>

            {/* Total + Contribution Sources rollup */}
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-1 bg-gradient-to-br from-emerald-600 to-teal-600 text-white border-0" data-testid="donations-total-card">
                <CardContent className="p-6">
                  <p className="text-sm text-emerald-100">Total Charitable Contributions</p>
                  <p className="text-4xl font-bold mt-1" data-testid="donations-total">
                    ${summary?.total_charitable_contributions?.toFixed(2) || '0.00'}
                  </p>
                  <div className="mt-5 pt-4 border-t border-white/20 flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold" data-testid="donations-contributors">{summary?.unique_contributors ?? 0}</p>
                      <p className="text-xs text-emerald-100">people contributed</p>
                    </div>
                    {showNewReturning && (
                      <p className="text-xs text-emerald-100 text-right leading-5">
                        {summary.new_contributors} new<br />{summary.returning_contributors} returning
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2" data-testid="contribution-sources">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PiggyBank className="w-5 h-5 text-emerald-600" />
                    Contribution Sources
                  </CardTitle>
                  <CardDescription>How your Total Charitable Contributions roll up</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stacked mix bar */}
                  <div className="flex h-3 rounded-full overflow-hidden bg-gray-100" data-testid="source-mix-bar">
                    <div className="bg-blue-500" style={{ width: `${pct(summary?.round_ups)}%` }} />
                    <div className="bg-indigo-500" style={{ width: `${pct(summary?.direct_gifts)}%` }} />
                    <div className="bg-purple-500" style={{ width: `${pct(summary?.grocer_partner)}%` }} />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {sources.map((s) => {
                      const Icon = s.icon;
                      const val = summary?.[s.key] || 0;
                      return (
                        <div key={s.key} className="p-3 rounded-lg border bg-gray-50" data-testid={`source-${s.key}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                            <Icon className={`w-4 h-4 ${s.text}`} />
                            <span className="text-sm font-medium text-gray-700">{s.label}</span>
                          </div>
                          <p className="text-xl font-bold text-gray-900 mt-1">${val.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{pct(val)}% of total · {s.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Impact */}
            <Card data-testid="donations-impact">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-emerald-600" />
                  Community Impact ({PERIODS.find(p => p.key === period)?.label})
                </CardTitle>
                <CardDescription>Food rescued through orders designating your organization</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-700">{(impact?.charitable_meals_funded ?? 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-600">meals funded</p>
                  </div>
                  <div className="p-4 bg-teal-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-teal-700">{(impact?.lbs_diverted ?? 0).toLocaleString()} lbs</p>
                    <p className="text-xs text-gray-600">food diverted from landfill</p>
                  </div>
                  <div className="p-4 bg-cyan-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-cyan-700">{(impact?.co2e_avoided_kg ?? 0).toLocaleString()} kg</p>
                    <p className="text-xs text-gray-600">CO₂e avoided</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Named Retailer Donors */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5 text-purple-600" />
                  Retailer Donors ({retailers.count})
                </CardTitle>
                <CardDescription>
                  Named retailer partners and how much each has contributed
                </CardDescription>
              </CardHeader>
              <CardContent>
                {retailers.retailers?.length > 0 ? (
                  <div className="divide-y" data-testid="retailer-donor-list">
                    {retailers.retailers.map((retailer) => (
                      <div key={retailer.id} className="py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-purple-600 font-bold">
                              {(retailer.store_name || retailer.name || 'R')[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{retailer.store_name || retailer.name}</p>
                            <p className="text-xs text-gray-500">{retailer.order_count || 0} contributing orders</p>
                          </div>
                        </div>
                        <p className="font-semibold text-emerald-600">${(retailer.total_contributed ?? 0).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Store className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No retailer donors yet</p>
                    <p className="text-sm">Retailers can designate your organization during registration</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly / Quarterly Chart */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-emerald-600" />
                      Contributions Over Time
                    </CardTitle>
                    <CardDescription>{chartMode === 'quarterly' ? 'By quarter' : 'By month'}, past 12 months</CardDescription>
                  </div>
                  <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="chart-mode-toggle">
                    <button
                      onClick={() => setChartMode('monthly')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${chartMode === 'monthly' ? 'bg-white shadow text-emerald-700 font-medium' : 'text-gray-500'}`}
                      data-testid="chart-mode-monthly"
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setChartMode('quarterly')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${chartMode === 'quarterly' ? 'bg-white shadow text-emerald-700 font-medium' : 'text-gray-500'}`}
                      data-testid="chart-mode-quarterly"
                    >
                      Quarterly
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="space-y-3" data-testid="contributions-chart">
                    {chartData.map((row, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className="w-20 text-sm text-gray-600">{row.label}</div>
                        <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                            style={{ width: `${(row.amount / chartMax) * 100}%` }}
                          />
                        </div>
                        <div className="w-24 text-right">
                          <span className="font-semibold text-gray-900">${row.amount.toFixed(2)}</span>
                          <span className="text-xs text-gray-500 ml-1">({row.orders})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No donation data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents */}
            <Card data-testid="charity-documents">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Receipts & Tax Statements
                </CardTitle>
                <CardDescription>Download donation receipts and year-end statements for your records</CardDescription>
              </CardHeader>
              <CardContent>
                {documents.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {documents.map((doc) => {
                      const key = `${doc.type}-${doc.period}`;
                      return (
                        <div key={key} className="p-3 border rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{doc.label}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDoc(doc)}
                            disabled={downloading === key}
                            data-testid={`download-${key}`}
                          >
                            {downloading === key
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Download className="w-4 h-4" />}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No documents yet</p>
                    <p className="text-sm">Receipts appear here once you receive donations</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payout Ledger */}
            <Card data-testid="payout-ledger">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-emerald-600" />
                  Payout Ledger
                </CardTitle>
                <CardDescription>What has been deposited via Stripe and what is still pending</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                    <p className="text-xs text-gray-600">Deposited to date</p>
                    <p className="text-2xl font-bold text-emerald-700" data-testid="payout-total-paid">
                      ${(payouts?.total_paid ?? summary?.total_paid ?? 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-100">
                    <p className="text-xs text-gray-600">Pending payout</p>
                    <p className="text-2xl font-bold text-orange-600" data-testid="payout-pending">
                      ${(payouts?.pending_payout ?? summary?.pending_payout ?? 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                {payouts?.payouts?.length > 0 ? (
                  <div className="divide-y border rounded-lg" data-testid="payout-ledger-list">
                    {payouts.payouts.map((p) => {
                      const done = p.status === 'completed';
                      const when = p.completed_at || p.created_at;
                      return (
                        <div key={p.id} className="p-3 flex items-center justify-between gap-3" data-testid={`payout-row-${p.id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${done ? 'bg-emerald-100' : 'bg-orange-100'}`}>
                              {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Clock className="w-4 h-4 text-orange-600" />}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">${p.amount.toFixed(2)}</p>
                              <p className="text-xs text-gray-500">
                                {when ? new Date(when).toLocaleDateString() : '—'} · {p.method === 'manual' ? 'Bank transfer' : 'Stripe'}
                                {p.reference_number ? ` · ref ${p.reference_number}` : ''}
                              </p>
                            </div>
                          </div>
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                            {done ? 'Deposited' : 'Pending'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 border rounded-lg" data-testid="payout-ledger-empty">
                    <Banknote className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No payouts yet</p>
                    <p className="text-sm">Payouts appear here once DealShaq deposits your contributions</p>
                  </div>
                )}
                <p className="text-xs text-gray-400">
                  Payouts are processed monthly for balances over $25.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
